/*
  Frequently Bought Together: tick boxes, running total, one add button.

  The complements come from the Search & Discovery app's complementary_products metafield,
  fetched through Shopify's product recommendations route with intent=complementary (see
  sections/shintop-fbt.liquid). This file lazily fetches that route, appends the returned
  Liquid-rendered cards to the list, reveals the band, and only then wires the interaction —
  so a product with no curated complements never shows a "bought together" row of one.

  The total is a sum, so unlike the per-unit price it cannot be pre-rendered per variant —
  this is the one place Shintop formats money in JavaScript. It does so using the shop's own
  money_format string rather than Intl, because Intl renders AUD as "A$35.10" where Shopify
  renders "$35.10", and the page must not disagree with the cart.

  Adds every ticked item in a single POST to /cart/add.js, then hands the response to the
  shared after-write step, assets/shintop-cart.js.
*/
if (!customElements.get('shintop-fbt')) {
  customElements.define(
    'shintop-fbt',
    class ShintopFbt extends HTMLElement {
      connectedCallback() {
        this.band = this.querySelector('[data-shintop-fbt-band]');
        this.list = this.querySelector('[data-shintop-fbt-list]');
        if (!this.dataset.url || !this.band || !this.list) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 600px 0px' },
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      // Fetch the curated complements; without them the band stays hidden for good.
      async load() {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const cards = doc.querySelectorAll('.shintop-fbt__item');
          if (cards.length === 0) return;

          cards.forEach((card) => this.list.appendChild(card));
          // Wired BEFORE it is revealed: the total is a live region, and a region that changes as
          // it appears announces "Total $…" to a screen-reader user scrolling past (S06-017).
          this.wire();
          this.band.removeAttribute('hidden');
        } catch {
          /* leave the band hidden */
        }
      }

      wire() {
        this.checkboxes = Array.from(this.querySelectorAll('[data-shintop-fbt-checkbox]'));
        this.totalEl = this.querySelector('[data-shintop-fbt-total]');
        this.addButton = this.querySelector('[data-shintop-fbt-add]');
        this.errorEl = this.querySelector('[data-shintop-fbt-error]');
        this.strError = this.dataset.strError || '';
        this.strNone = this.dataset.strNone || '';
        if (this.checkboxes.length === 0 || !this.addButton) return;

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

        this.checkboxes.forEach((box) => box.addEventListener('change', () => this.updateTotal()));
        this.addButton.addEventListener('click', () => this.addSelected());
        this.updateTotal();
      }

      // `checkboxes` exists only once wire() runs; a band with nothing to recommend never wires.
      // A throwing getter here crashed axe (and so Lighthouse's a11y audit), which reads
      // `.selected` on every element it walks (B657).
      get selected() {
        return (this.checkboxes || []).filter((box) => box.checked);
      }

      /*
        THE TOTAL IS A LIVE REGION, AND NOTHING TICKED SAYS SO (audit 2026-10-14, S06-017). A box
        ticked or unticked changed the sum and could switch the button off, and a screen-reader
        user heard neither; with nothing ticked the band showed "$0.00" over a dead button with
        no reason given. `role="status"` is on the total's paragraph (sections/shintop-fbt.liquid),
        so whatever this writes is announced — the sum, or `shintop.fbt.none_selected` in its
        place. The sentence sits INSIDE the value's own box, which keeps the value's line height,
        so the button below does not move when the last box is unticked.
      */
      updateTotal() {
        const none = this.selected.length === 0;
        if (this.totalEl) {
          if (none && this.strNone) {
            const note = document.createElement('span');
            note.className = 'shintop-fbt__none';
            note.textContent = this.strNone;
            this.totalEl.replaceChildren(note);
          } else {
            const cents = this.selected.reduce((sum, box) => sum + Number(box.dataset.price || 0), 0);
            this.totalEl.textContent = this.formatMoney(cents);
          }
        }
        this.addButton.disabled = none;
      }

      formatMoney(cents) {
        const value = Number(cents) / 100;

        const withSeparators = (num, decimals, thousands, decimal) => {
          const fixed = num.toFixed(decimals);
          const [whole, frac] = fixed.split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return frac ? `${grouped}${decimal}${frac}` : grouped;
        };

        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, token) => {
          switch (token) {
            case 'amount_no_decimals':
              return withSeparators(value, 0, ',', '.');
            case 'amount_with_comma_separator':
              return withSeparators(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withSeparators(value, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return withSeparators(value, 2, "'", '.');
            default:
              return withSeparators(value, 2, ',', '.');
          }
        });
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || this.strError;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      async addSelected() {
        const items = this.selected.map((box) => ({
          id: Number(box.dataset.variantId),
          quantity: 1,
        }));
        if (items.length === 0) return;

        this.hideError();
        this.addButton.setAttribute('aria-busy', 'true');
        this.addButton.disabled = true;

        // PER-01: ask for the rendered cart sections in the SAME add request, so the whole flow
        // is one round trip instead of add-then-fetch. Which sections is the helper's call.
        const sectionIds = window.shintopCart ? window.shintopCart.sections() : [];

        try {
          const body = { items };
          if (sectionIds.length) {
            body.sections = sectionIds;
            body.sections_url = window.location.pathname;
          }
          const response = await fetch(`${this.dataset.cartUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            // UX-03: surface the real reason (sold out, line cap) instead of swallowing it.
            let message = '';
            try {
              const failed = await response.json();
              message = failed.description || failed.message || '';
            } catch {
              /* non-JSON body — fall back to the generic string */
            }
            this.showError(message);
            return;
          }

          this.afterAdd(await response.json());
        } catch (error) {
          console.warn('[shintop] could not add the selected items:', error);
          this.showError();
        } finally {
          this.addButton.removeAttribute('aria-busy');
          this.updateTotal();
        }
      }

      /*
        Everything after a landed add is the shared after-write step (assets/shintop-cart.js): the
        drawer and the bubble repainted from this response's sections, the drawer opened with THIS
        button as its opener so that closing it gives focus back here (audit 2026-10-14, S06-016 —
        it used to land on nothing), and one cartUpdate publish carrying the whole cart.

        It replaced a loop of this file's own that rendered `#CartDrawer` whole — throwing away the
        overlay Dawn binds its close-on-click to once, so the backdrop stopped closing the drawer —
        and looked for the bubble by an id the section response does not carry (S03-004).
      */
      afterAdd(parsed) {
        if (!window.shintopCart) {
          window.location.href = window.routes.cart_url;
          return;
        }
        window.shintopCart.afterCartWrite(parsed, { source: 'shintop-fbt', open: true, opener: this.addButton });
      }
    },
  );
}
